import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountsActiveLicenses} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {useAppDispatch} from 'src/store/hooks';

import {SELECTED_COLUMNS} from '../constants';
import {
    userConferencesLicenseActions,
    userConferencesLicenseDataSelector,
    userConferencesLicenseResultSelector,
} from '../slices/user-conferences-license.slice';
import {userConferencesStateSelector} from '../slices/user-conferences-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserConferencesCacheKeys} from '../typedef';
import type {LicenseSearchInfo} from 'src/openapi/admin/models/LicenseSearchInfo';


type Props = {
    accountIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserConferencesLicense = ({accountIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(() => apiDispatch(getAccountsActiveLicenses({
        requestBody: accountIds,
    })), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, LicenseSearchInfo>,
        dataCacheKeys: UserConferencesCacheKeys,
    ) => {
        dispatch(userConferencesLicenseActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(() => checkShouldLoadColumn(columnId, SELECTED_COLUMNS), [columnId]);

    return useCachedTable({
        dataSelector: userConferencesLicenseDataSelector,
        stateSelector: userConferencesStateSelector,
        resultSelector: userConferencesLicenseResultSelector,
        actions: userConferencesLicenseActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
